import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, TextField, Button, Paper, Switch, FormControlLabel,
  Grid, CircularProgress, LinearProgress, Autocomplete, Chip, IconButton, ToggleButton,
  ToggleButtonGroup, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, FormHelperText
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TodayIcon from '@mui/icons-material/Today';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RepeatIcon from '@mui/icons-material/Repeat';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import API_ENDPOINTS from '../config/apiConfig';
import { useTranslation } from '../i18n/i18n';
import { useBranch } from '../context/BranchContext';
import { DAY_KEYS, addDays, getMonday, getWeekDays, formatDateISO, getMonthDays, getWeeksInMonth, buildVacationDateSet } from '../utils/dateUtils';
import FixedSnackbar from './FixedSnackbar';

const DEFAULT_DAYS = {
  monday:    { active: true,  start: '09:00', end: '17:00', branch: '' },
  tuesday:   { active: true,  start: '09:00', end: '17:00', branch: '' },
  wednesday: { active: true,  start: '09:00', end: '17:00', branch: '' },
  thursday:  { active: true,  start: '09:00', end: '17:00', branch: '' },
  friday:    { active: true,  start: '09:00', end: '17:00', branch: '' },
  saturday:  { active: false, start: '09:00', end: '17:00', branch: '' },
  sunday:    { active: false, start: '09:00', end: '17:00', branch: '' }
};

const INACTIVE_DAYS = Object.fromEntries(
  Object.entries(DEFAULT_DAYS).map(([k, v]) => [k, { ...v, active: false }])
);

const EMPTY_ERRORS = {};

export default function ScheduleManager({ hirees }) {
  const { t, lang } = useTranslation();
  const { branches: allBranches } = useBranch();
  const [selectedEmail, setSelectedEmail] = useState('');
  const [employeeBranches, setEmployeeBranches] = useState([]);
  const [mode, setMode] = useState('recurring');
  const [currentWeek, setCurrentWeek] = useState(() => getMonday(formatDateISO(new Date())));
  const [scheduleName, setScheduleName] = useState('Standard');
  const [days, setDays] = useState(INACTIVE_DAYS);
  const [monthlyOverrides, setMonthlyOverrides] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [hasLoadedSchedule, setHasLoadedSchedule] = useState(false);
  const [vacations, setVacations] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [copySource, setCopySource] = useState(null);
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
  const [branchErrors, setBranchErrors] = useState(EMPTY_ERRORS);
  const fetchIdRef = useRef(0);

  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());

  const dayLabels = {
    monday: t('calendar_day_monday'),
    tuesday: t('calendar_day_tuesday'),
    wednesday: t('calendar_day_wednesday'),
    thursday: t('calendar_day_thursday'),
    friday: t('calendar_day_friday'),
    saturday: t('calendar_day_saturday'),
    sunday: t('calendar_day_sunday')
  };

  const resolveEmployeeBranches = useCallback((email) => {
    const hiree = (hirees || []).find((h) => h.email === email);
    if (!hiree) return [];
    const ids = hiree.branches || [];
    return ids.map((id) => {
      const b = allBranches.find((br) => (br.branch_id || br.code) === id);
      return { id, name: b ? b.name : id };
    });
  }, [hirees, allBranches]);

  const applySingleBranchToDays = (daysObj, empBranches) => {
    if (empBranches.length !== 1) return daysObj;
    const branchId = empBranches[0].id;
    const updated = {};
    for (const dk of DAY_KEYS) {
      const d = daysObj[dk];
      if (d && d.active !== false) {
        updated[dk] = { ...d, branch: branchId };
      } else {
        updated[dk] = d ? { ...d } : d;
      }
    }
    return updated;
  };

  const monthLabel = new Date(viewYear, viewMonth).toLocaleDateString(
    lang === 'spanish' ? 'es-MX' : 'en-US',
    { month: 'long', year: 'numeric' }
  );

  const weekLabels = (weekStart) => {
    const d1 = new Date(weekStart + 'T00:00:00');
    const d7 = new Date(addDays(weekStart, 6) + 'T00:00:00');
    const opts = { month: 'short', day: 'numeric' };
    const locale = lang === 'spanish' ? 'es-MX' : 'en-US';
    return `${d1.toLocaleDateString(locale, opts)} – ${d7.toLocaleDateString(locale, opts)}`;
  };

  const fetchMonthlyOverrides = useCallback(async () => {
    if (!selectedEmail) return;
    try {
      const monthStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;
      const res = await fetch(`${API_ENDPOINTS.SCHEDULE_MONTHLY(selectedEmail)}?month=${monthStr}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const map = {};
        (data.overrides || []).forEach((o) => { map[o.week_start] = o; });
        setMonthlyOverrides(map);
      }
    } catch (err) {
      console.error('Error fetching monthly overrides:', err);
    }
  }, [selectedEmail, viewYear, viewMonth]);

  useEffect(() => { fetchMonthlyOverrides(); }, [fetchMonthlyOverrides]);

  const fetchVacations = useCallback(async () => {
    if (!selectedEmail) { setVacations([]); return; }
    try {
      const res = await fetch(API_ENDPOINTS.VACATION_GET(selectedEmail), { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setVacations(data.vacations || []);
      }
    } catch (err) {
      console.error('Error fetching vacations:', err);
    }
  }, [selectedEmail]);

  useEffect(() => { fetchVacations(); }, [fetchVacations]);

  const vacationSet = React.useMemo(() => buildVacationDateSet(vacations), [vacations]);

  const fetchScheduleForContext = useCallback(async () => {
    if (!selectedEmail) return;
    const fetchId = ++fetchIdRef.current;
    setBranchErrors((prev) => (Object.keys(prev).length > 0 ? EMPTY_ERRORS : prev));
    const empBranches = resolveEmployeeBranches(selectedEmail);
    const isStale = () => fetchId !== fetchIdRef.current;
    try {
      if (mode === 'recurring') {
        const res = await fetch(API_ENDPOINTS.SCHEDULE_GET(selectedEmail), { credentials: 'include' });
        if (isStale()) return;
        if (res.ok) {
          const data = await res.json();
          if (isStale()) return;
          setScheduleName(data.name || 'Standard');
          let loadedDays = data.days || INACTIVE_DAYS;
          if (empBranches.length === 1) {
            loadedDays = applySingleBranchToDays(loadedDays, empBranches);
          }
          setDays(loadedDays);
          setHasLoadedSchedule(true);
        } else {
          setScheduleName('Standard');
          setDays(INACTIVE_DAYS);
          setHasLoadedSchedule(false);
        }
      } else {
        const res = await fetch(`${API_ENDPOINTS.SCHEDULE_WEEKLY_GET(selectedEmail)}?week_start=${currentWeek}`, { credentials: 'include' });
        if (isStale()) return;
        if (res.ok) {
          const data = await res.json();
          if (isStale()) return;
          if (data.schedule) {
            setScheduleName(data.schedule.name || 'Weekly');
            let loadedDays = data.schedule.days || INACTIVE_DAYS;
            if (empBranches.length === 1) {
              loadedDays = applySingleBranchToDays(loadedDays, empBranches);
            }
            setDays(loadedDays);
            setHasLoadedSchedule(true);
          } else {
            const defRes = await fetch(API_ENDPOINTS.SCHEDULE_GET(selectedEmail), { credentials: 'include' });
            if (isStale()) return;
            if (defRes.ok) {
              const defData = await defRes.json();
              if (isStale()) return;
              setScheduleName(defData.name || 'Standard');
              let loadedDays = defData.days || INACTIVE_DAYS;
              if (empBranches.length === 1) {
                loadedDays = applySingleBranchToDays(loadedDays, empBranches);
              }
              setDays(loadedDays);
              setHasLoadedSchedule(true);
            } else {
              setScheduleName('Standard');
              setDays(INACTIVE_DAYS);
              setHasLoadedSchedule(false);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error fetching schedule:', err);
    } finally {
      if (!isStale()) setFetching(false);
    }
  }, [selectedEmail, mode, currentWeek, resolveEmployeeBranches]);

  useEffect(() => { fetchScheduleForContext(); }, [fetchScheduleForContext]);

  const handleSave = async () => {
    const errors = {};
    DAY_KEYS.forEach((dk) => {
      const d = days[dk];
      if (d && d.active !== false && !(d.branch || '').trim()) {
        errors[dk] = true;
      }
    });
    if (Object.keys(errors).length > 0) {
      setBranchErrors(errors);
      const missingDays = DAY_KEYS.filter((dk) => errors[dk]).map((dk) => dayLabels[dk]).join(', ');
      setSnackbar({
        open: true,
        message: t('calendar_schedule_branch_missing_days', { days: missingDays }) || `Missing branch for: ${missingDays}`,
        severity: 'error'
      });
      return;
    }
    setBranchErrors({});
    setLoading(true);
    try {
      let res;
      if (mode === 'recurring') {
        res = await fetch(API_ENDPOINTS.SCHEDULE_SAVE(selectedEmail), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: scheduleName, days })
        });
      } else {
        res = await fetch(API_ENDPOINTS.SCHEDULE_WEEKLY_SAVE(selectedEmail), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ week_start: currentWeek, name: scheduleName, days })
        });
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'calendar_schedule_error');
      }
      setSnackbar({ open: true, message: t('calendar_schedule_saved'), severity: 'success' });
      fetchMonthlyOverrides();
    } catch (err) {
      setSnackbar({ open: true, message: t(err.message) || t('calendar_schedule_error'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOverride = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_ENDPOINTS.SCHEDULE_WEEKLY_DELETE(selectedEmail)}?week_start=${currentWeek}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'calendar_schedule_error');
      }
      setSnackbar({ open: true, message: t('calendar_schedule_saved'), severity: 'success' });
      fetchMonthlyOverrides();
      fetchScheduleForContext();
    } catch (err) {
      setSnackbar({ open: true, message: t(err.message) || t('calendar_schedule_error'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    const daysCopy = {};
    for (const dk of DAY_KEYS) {
      daysCopy[dk] = days[dk] ? { ...days[dk] } : days[dk];
    }
    setCopySource({ week: currentWeek, name: scheduleName, days: daysCopy });
    setSnackbar({ open: true, message: t('calendar_schedule_copied') || 'Schedule copied.', severity: 'info' });
  };

  const handlePaste = async () => {
    if (!copySource) return;
    setPasteDialogOpen(false);
    setLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.SCHEDULE_COPY(selectedEmail), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ from_week: copySource.week, to_week: currentWeek })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'calendar_schedule_error');
      }
      setSnackbar({ open: true, message: t('calendar_schedule_saved'), severity: 'success' });
      fetchMonthlyOverrides();
      fetchScheduleForContext();
    } catch (err) {
      setSnackbar({ open: true, message: t(err.message) || t('calendar_schedule_error'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const clampEndTimeAfterStart = (start, end) => {
    if (start && end && end <= start) {
      const [h, m] = start.split(':').map(Number);
      const nextH = h + 1 > 23 ? 23 : h + 1;
      return `${String(nextH).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
    return end;
  };

  const handleDayChange = (dayKey, field, value) => {
    setDays((prev) => {
      const day = prev[dayKey];
      if (field === 'start') {
        const newEnd = clampEndTimeAfterStart(value, day.end);
        return { ...prev, [dayKey]: { ...day, start: value, end: newEnd } };
      }
      if (field === 'end') {
        const clamped = clampEndTimeAfterStart(day.start, value);
        return { ...prev, [dayKey]: { ...day, end: clamped } };
      }
      if (field === 'active') {
        const updated = { ...prev, [dayKey]: { ...day, active: value } };
        if (value && employeeBranches.length === 1) {
          updated[dayKey].branch = employeeBranches[0].id;
        }
        return updated;
      }
      return { ...prev, [dayKey]: { ...day, [field]: value } };
    });
    if (field === 'branch') {
      setBranchErrors((prev) => {
        const next = { ...prev };
        delete next[dayKey];
        return next;
      });
    }
  };

  const computeDayHours = (start, end) => {
    if (!start || !end) return 0;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    return Math.max(0, (eh * 60 + em - sh * 60 - sm) / 60);
  };

  const hireeOptions = (hirees || []).map((h) => ({ label: h.fullname || h.email, email: h.email }));
  const weeks = getWeeksInMonth(viewYear, viewMonth);
  const hasOverride = !!monthlyOverrides[currentWeek];

  const totalWeekHours = DAY_KEYS.reduce((sum, dk) => {
    const d = days[dk];
    return sum + (d && d.active !== false ? computeDayHours(d.start, d.end) : 0);
  }, 0);

  return (
    <Box>
      {/* Hiree Selector */}
      <Autocomplete
        options={hireeOptions}
        getOptionLabel={(opt) => opt.label}
        onChange={(_, val) => {
          if (val) {
            const empBranches = resolveEmployeeBranches(val.email);
            setEmployeeBranches(empBranches);
            setSelectedEmail(val.email);
            setFetching(true);
            setCopySource(null);
            setHasLoadedSchedule(false);
            setBranchErrors({});
            if (empBranches.length === 1) {
              setDays((prev) => applySingleBranchToDays(prev, empBranches));
            }
          } else {
            setSelectedEmail('');
            setEmployeeBranches([]);
            setDays(INACTIVE_DAYS);
            setCopySource(null);
            setHasLoadedSchedule(false);
            setBranchErrors({});
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('calendar_schedule_title')}
            size="small"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <FilterListIcon fontSize="small" sx={{ color: '#62B6CB', mr: 0.5 }} />
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, opt) => (
          <li {...props} key={opt.email}>
            <Box>
              <Typography variant="body1">{opt.label}</Typography>
              <Typography variant="caption" color="text.secondary">{opt.email}</Typography>
            </Box>
          </li>
        )}
        sx={{ mb: 3, minWidth: { xs: '100%', sm: 200 }, maxWidth: 350 }}
      />

      {selectedEmail && (
        <>
          {/* Mode Toggle & Week Navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_, v) => { if (v) setMode(v); }}
              size="small"
            >
              <ToggleButton value="recurring" sx={{ textTransform: 'none' }}>
                <RepeatIcon sx={{ mr: 0.5, fontSize: 18 }} />
                {t('calendar_schedule_recurring') || 'Recurring'}
              </ToggleButton>
              <ToggleButton value="weekly" sx={{ textTransform: 'none' }}>
                <EditIcon sx={{ mr: 0.5, fontSize: 18 }} />
                {t('calendar_schedule_weekly') || 'Weekly'}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Vacation Display */}
          {vacations.filter(v => v.status === 'approved').length > 0 && (
            <Paper
              elevation={0}
              sx={{
                mb: 3,
                p: 2,
                bgcolor: 'primary.main' + '08',
                border: '1px solid',
                borderColor: 'primary.light',
                borderRadius: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <EventBusyIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {t('calendar_schedule_vacation')}:
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {vacations.filter(v => v.status === 'approved').map((v) => (
                  <Chip
                    key={v._id}
                    label={`${v.start_date} – ${v.end_date} (${v.days_count}d)`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ height: 28 }}
                  />
                ))}
              </Box>
            </Paper>
          )}

          {/* Week Selection (Weekly Mode) */}
          {mode === 'weekly' && (
            <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: 1, borderColor: 'divider' }}>
              <Button
                size="small"
                startIcon={<TodayIcon />}
                onClick={() => {
                  const today = getMonday(formatDateISO(new Date()));
                  setCurrentWeek(today);
                  const todayDate = new Date(today + 'T00:00:00');
                  if (todayDate.getMonth() !== viewMonth || todayDate.getFullYear() !== viewYear) {
                    setViewYear(todayDate.getFullYear());
                    setViewMonth(todayDate.getMonth());
                  }
                }}
                sx={{ textTransform: 'none', mb: 1 }}
              >
                {t('calendar_nav_thisweek')}
              </Button>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1.5 }}>
                <IconButton onClick={() => {
                  const m = new Date(viewYear, viewMonth - 1);
                  setViewYear(m.getFullYear());
                  setViewMonth(m.getMonth());
                  const wks = getWeeksInMonth(m.getFullYear(), m.getMonth());
                  if (wks.length) setCurrentWeek(wks[wks.length - 1]);
                }} size="small">
                  <ChevronLeftIcon />
                </IconButton>
                <Typography variant="body2" sx={{ minWidth: { xs: 100, sm: 140 }, textAlign: 'center', fontWeight: 500 }}>
                  {monthLabel}
                </Typography>
                <IconButton onClick={() => {
                  const m = new Date(viewYear, viewMonth + 1);
                  setViewYear(m.getFullYear());
                  setViewMonth(m.getMonth());
                  const wks = getWeeksInMonth(m.getFullYear(), m.getMonth());
                  if (wks.length) setCurrentWeek(wks[0]);
                }} size="small">
                  <ChevronRightIcon />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <IconButton
                  size="small"
                  onClick={() => {
                    const prev = addDays(currentWeek, -7);
                    setCurrentWeek(prev);
                    const prevDate = new Date(prev + 'T00:00:00');
                    if (prevDate.getMonth() !== viewMonth || prevDate.getFullYear() !== viewYear) {
                      setViewYear(prevDate.getFullYear());
                      setViewMonth(prevDate.getMonth());
                    }
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', flex: 1, mx: 1 }}>
                  {weeks.map((w) => (
                    <Chip
                      key={w}
                      label={weekLabels(w)}
                      onClick={() => setCurrentWeek(w)}
                      color={currentWeek === w ? 'primary' : monthlyOverrides[w] ? 'success' : 'default'}
                      variant={currentWeek === w ? 'filled' : 'outlined'}
                      size="small"
                      sx={{ height: 32, fontWeight: 500 }}
                    />
                  ))}
                </Box>
                <IconButton
                  size="small"
                  onClick={() => {
                    const next = addDays(currentWeek, 7);
                    setCurrentWeek(next);
                    const nextDate = new Date(next + 'T00:00:00');
                    if (nextDate.getMonth() !== viewMonth || nextDate.getFullYear() !== viewYear) {
                      setViewYear(nextDate.getFullYear());
                      setViewMonth(nextDate.getMonth());
                    }
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>
              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t('calendar_schedule_selected_week') || 'Selected:'} {weekLabels(currentWeek)}
                </Typography>
                {hasOverride ? (
                  <Chip label={t('calendar_schedule_has_override') || 'Custom schedule'} color="success" size="small" sx={{ height: 24 }} />
                ) : (
                  <Chip label={t('calendar_schedule_recurring') || 'Using recurring schedule'} size="small" variant="outlined" sx={{ height: 24 }} />
                )}
              </Box>
            </Paper>
          )}

          {/* Copy/Paste/Revert Actions */}
          {mode === 'weekly' && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button size="small" startIcon={<ContentCopyIcon />} onClick={handleCopy} sx={{ textTransform: 'none' }}>
                {t('calendar_schedule_copy') || 'Copy'}
              </Button>
              {copySource && (
                <Button size="small" startIcon={<ContentPasteIcon />} onClick={() => setPasteDialogOpen(true)} sx={{ textTransform: 'none' }}>
                  {t('calendar_schedule_paste') || 'Paste'}
                </Button>
              )}
              {hasOverride && (
                <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={handleDeleteOverride} sx={{ textTransform: 'none' }}>
                  {t('calendar_schedule_revert') || 'Revert to recurring'}
                </Button>
              )}
            </Box>
          )}

          {/* Schedule Grid */}
          <Box sx={{ position: 'relative' }}>
            {fetching && (
              <Box sx={{
                position: 'absolute', top: 0, left: 0, right: 0,
                zIndex: 1, borderRadius: 2, overflow: 'hidden'
              }}>
                <LinearProgress sx={{ height: 3 }} />
              </Box>
            )}
            <Box>
              <TextField
                label={t('calendar_schedule_name')}
                value={scheduleName}
                onChange={(e) => setScheduleName(e.target.value)}
                size="small"
                fullWidth
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': { borderRadius: 1.5 }
                }}
              />
              {!hasLoadedSchedule && !fetching && (
                <Chip label={t('calendar_schedule_no_schedule')} size="small" color="warning" variant="outlined" sx={{ mb: 2 }} />
              )}

              <Grid container spacing={2}>
                {DAY_KEYS.map((dayKey, idx) => {
                  const dayData = days[dayKey] || {};
                  const weekDates = getWeekDays(currentWeek);
                  const dateForDay = weekDates[idx];
                  const isVacation = mode === 'weekly' && vacationSet.has(dateForDay);
                  return (
                    <Grid item xs={12} sm={6} md key={dayKey}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: isVacation ? 'primary.main' + '08' : 'background.paper',
                          borderColor: isVacation ? 'primary.light' : 'divider',
                          transition: 'all 0.15s ease-in-out',
                          '&:hover': {
                            borderColor: 'primary.light',
                            boxShadow: 1
                          }
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={dayData.active !== false}
                              onChange={(e) => handleDayChange(dayKey, 'active', e.target.checked)}
                              size="small"
                              disabled={isVacation}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {dayLabels[dayKey]}
                              </Typography>
                              {mode === 'weekly' && dateForDay && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                                  {new Date(dateForDay + 'T00:00:00').toLocaleDateString(lang === 'spanish' ? 'es-MX' : 'en-US', { month: 'short', day: 'numeric' })}
                                </Typography>
                              )}
                            </Box>
                          }
                          sx={{ mb: 1 }}
                        />
                        {isVacation ? (
                          <Chip label={t('calendar_schedule_vacation')} size="small" color="primary" variant="outlined" sx={{ height: 26 }} />
                        ) : dayData.active !== false ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.25 }}>
                                {t('calendar_schedule_start')}
                              </Typography>
                              <input
                                type="time"
                                value={dayData.start || '09:00'}
                                onChange={(e) => handleDayChange(dayKey, 'start', e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  fontFamily: 'inherit',
                                  border: '1px solid #c4c4c4',
                                  borderRadius: '8px',
                                  boxSizing: 'border-box',
                                  height: '40px',
                                  outline: 'none',
                                  transition: 'border-color 0.15s'
                                }}
                              />
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.25 }}>
                                {t('calendar_schedule_end')}
                              </Typography>
                              <input
                                type="time"
                                value={dayData.end || '17:00'}
                                onChange={(e) => handleDayChange(dayKey, 'end', e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  fontFamily: 'inherit',
                                  border: '1px solid #c4c4c4',
                                  borderRadius: '8px',
                                  boxSizing: 'border-box',
                                  height: '40px',
                                  outline: 'none',
                                  transition: 'border-color 0.15s'
                                }}
                              />
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.25 }}>
                                {t('calendar_schedule_branch')}
                              </Typography>
                              <FormControl
                                size="small"
                                fullWidth
                                error={!!branchErrors[dayKey]}
                                disabled={employeeBranches.length <= 1}
                              >
                                {employeeBranches.length <= 1 ? (
                                  <TextField
                                    size="small"
                                    value={employeeBranches.length === 1 ? employeeBranches[0].name : ''}
                                    disabled
                                    fullWidth
                                    sx={{
                                      '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                                      '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.6)' }
                                    }}
                                  />
                                ) : (
                                  <Select
                                    value={(dayData.branch || '').trim()}
                                    onChange={(e) => handleDayChange(dayKey, 'branch', e.target.value)}
                                    displayEmpty
                                    sx={{ borderRadius: 1.5 }}
                                  >
                                    <MenuItem value="" disabled>
                                      <em>--</em>
                                    </MenuItem>
                                    {employeeBranches.map((b) => (
                                      <MenuItem key={b.id} value={b.id}>
                                        {b.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                )}
                                {branchErrors[dayKey] && (
                                  <FormHelperText>{t('calendar_schedule_branch_required')}</FormHelperText>
                                )}
                              </FormControl>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', textAlign: 'center' }}>
                              {computeDayHours(dayData.start, dayData.end).toFixed(1)} hrs
                            </Typography>
                          </Box>
                        ) : (
                          <Chip label={t('calendar_schedule_inactive')} size="small" sx={{ height: 26 }} />
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Weekly Hours Summary */}
              <Paper
                elevation={0}
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  textAlign: 'center',
                  border: 1,
                  borderColor: 'divider'
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {t('calendar_schedule_weekly_hours') || 'Total Hours This Week'}: {' '}
                  {totalWeekHours.toFixed(1)} hrs
                </Typography>
              </Paper>

              {/* Save Button */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={18} /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={loading}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: 'none',
                    '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }
                  }}
                >
                  {t('calendar_schedule_save')}
                </Button>
              </Box>
            </Box>
          </Box>
        </>
      )}

      {/* Paste Confirmation Dialog */}
      <Dialog open={pasteDialogOpen} onClose={() => setPasteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('calendar_schedule_paste') || 'Paste Schedule'}
          </Typography>
          <IconButton onClick={() => setPasteDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {(t('calendar_schedule_paste_confirm') || 'Paste schedule from {from} to {to}?')
              .replace('{from}', copySource ? weekLabels(copySource.week) : '')
              .replace('{to}', weekLabels(currentWeek))}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setPasteDialogOpen(false)} sx={{ textTransform: 'none' }}>
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            onClick={handlePaste}
            disabled={loading}
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
          >
            {loading ? <CircularProgress size={18} /> : t('calendar_schedule_paste') || 'Paste'}
          </Button>
        </DialogActions>
      </Dialog>

      <FixedSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
        autoHideDuration={4000}
      />
    </Box>
  );
}
